import React, { useState, useMemo, useRef, useEffect } from 'react';
import { layoutTwoLevel } from '../../utils/treemap';
import { SECTOR_FOR } from '../../utils/sectorMap';
import { renderHeatmapPng, shareHeatmapPng } from '../../utils/heatmapImage';
import './PortfolioHeatmap.css';

/**
 * Your holdings as a treemap, grouped by sector.
 *
 * The decision that makes this different from the market heatmap: tiles are
 * sized by POSITION VALUE, not market cap. A 2% slip in your largest holding
 * should look bigger than a 9% slip in a starter position, because it costs you
 * more. Sizing by market cap would just redraw the market inside your account.
 *
 * Colour answers "how am I doing", which has two honest readings, so it is a
 * toggle rather than a guess:
 *   - "Since you bought" (default) is total P&L from entry_price and
 *     current_price, both of which the snapshot already returns. Costs nothing.
 *   - "Today" needs live quotes, which the snapshot does NOT carry, so they are
 *     fetched the first time it is asked for and never on mount.
 */

// Same ramp as the market heatmap so the two read the same way. Thresholds
// widen for total P&L, where 3% is an unremarkable holding period rather than
// a big day.
function colorFor(pct, mode) {
  if (pct === null || pct === undefined) return '#475569';   // no data, not flat
  const [hi, mid] = mode === 'today' ? [3, 1] : [25, 8];
  if (pct >= hi) return '#10B981';
  if (pct >= mid) return '#34D399';
  if (pct >= 0) return '#6EE7B7';
  if (pct >= -mid) return '#FCA5A5';
  if (pct >= -hi) return '#EF4444';
  return '#DC2626';
}

const fmtPct = (v) =>
  v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

const fmtMoney = (v) =>
  v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v.toFixed(0)}`;

export default function PortfolioHeatmap({ items = [], scores = [], lastAnalyzed = null }) {
  const [mode, setMode] = useState('total');       // 'total' | 'today'
  const [collapsed, setCollapsed] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [width, setWidth] = useState(0);
  const [sharing, setSharing] = useState('');   // '' | 'working' | 'saved' | 'copied'
  const chartRef = useRef(null);

  // Sector from the scored payload when the API supplied one, shared table
  // otherwise. Unknown names land in "Other" rather than disappearing.
  const sectorOf = useMemo(() => {
    const fromApi = {};
    (scores || []).forEach((s) => {
      if (s?.symbol && s.sector) fromApi[s.symbol] = s.sector;
    });
    return (symbol) => fromApi[symbol] || SECTOR_FOR(symbol);
  }, [scores]);

  // The symbol SET, not the array identity. A parent re-render hands us a new
  // `items` array with the same contents; keying the fetch off that would make
  // this effect re-run for no reason.
  const symbolsKey = useMemo(
    () => (items || []).map((i) => i?.symbol).filter(Boolean).join(','),
    [items],
  );

  // Which symbol set we have already gone and fetched. A ref, NOT state, and
  // that is the whole fix for the bug this shipped with on 2026-09-23.
  //
  // It used to guard on `quotesLoading` while ALSO having `quotesLoading` in the
  // dependency array. So: the effect set loading true, the state change
  // re-triggered the effect, React ran the cleanup of the request still in
  // flight, `cancelled` flipped true, and when the fetch finally resolved it
  // threw its own result away. A self-cancelling effect. It hung on "Fetching
  // today's moves…" forever, on every device.
  //
  // Nothing in the dependency array below is written by this effect, which is
  // the property that actually prevents it.
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (mode !== 'today' || !symbolsKey) return undefined;
    if (fetchedFor.current === symbolsKey) return undefined;
    fetchedFor.current = symbolsKey;

    let cancelled = false;
    setQuotesLoading(true);
    (async () => {
      const found = {};
      try {
        const symbols = symbolsKey.split(',');
        for (let i = 0; i < symbols.length; i += 20) {
          const batch = symbols.slice(i, i + 20).join(',');
          const resp = await fetch(`/api/market/quotes?symbols=${batch}`);
          if (!resp.ok) continue;
          const data = await resp.json();
          (data.quotes || data || []).forEach((q) => {
            if (q.symbol) found[q.symbol] = q;
          });
        }
      } catch {
        // Keep whatever arrived. A tile with no quote greys out and reads "—",
        // which is honest; showing it as flat would not be.
      }
      if (!cancelled) {
        setQuotes(found);
        setQuotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, symbolsKey]);

  const { groups, totalValue, count, missingPrice } = useMemo(() => {
    // Read money off the SCORED payload, not off `items`.
    //
    // 2026-09-24. This used to recompute value and P&L from
    // items[].current_price, which is not reliably there: PortfolioEditor's
    // onSaved sets portfolioItems to {symbol, shares, entry_price} with no
    // price at all, and the analyze path stores the positions AS SUBMITTED.
    // So on a device that had not just run an analysis, current_price was
    // undefined, the code fell back to entry_price, and every position showed
    // a flat 0% — a confident wrong answer, which is worse than a blank.
    // That is why the same portfolio read differently on his phone and laptop.
    //
    // The backend already computes both numbers (concurrent_portfolio.py:
    // pnl_percent and position_value) and PortfolioContext maps them straight
    // onto each score. One source of truth, no per-device drift.
    const byScore = {};
    (scores || []).forEach((sc) => {
      if (sc?.symbol) byScore[sc.symbol] = sc;
    });

    const bySector = {};
    let total = 0;
    let n = 0;
    let missingPrice = 0;

    (items || []).forEach((it) => {
      if (!it?.symbol) return;
      const sc = byScore[it.symbol];
      const shares = Number(it.shares) || 0;
      const entry = Number(sc?.entry_price ?? it.entry_price) || 0;
      const current = Number(sc?.current_price ?? it.current_price) || 0;

      // Value: prefer the server's number, fall back to shares * price.
      let value = Number(sc?.position_value);
      if (!Number.isFinite(value) || value <= 0) value = shares * (current || entry);
      if (value <= 0) return;

      let pct = null;
      if (mode === 'today') {
        const q = quotes?.[it.symbol];
        const raw = q?.changePercentage ?? q?.changesPercentage ?? q?.change_percent;
        pct = typeof raw === 'number' ? raw : null;
      } else if (Number.isFinite(Number(sc?.pnl_percent))) {
        pct = Number(sc.pnl_percent);
      } else if (entry > 0 && current > 0) {
        pct = ((current - entry) / entry) * 100;
      }
      // Deliberately NOT defaulting to 0. A position we have no live price for
      // reads grey and "—", which is true, instead of flat, which is a claim.
      if (pct === null) missingPrice += 1;

      const sector = sectorOf(it.symbol);
      if (!bySector[sector]) bySector[sector] = [];
      bySector[sector].push({ name: it.symbol, size: value, value, pct });
      total += value;
      n += 1;
    });

    const sectorGroups = Object.entries(bySector)
      .map(([name, children]) => ({
        name,
        children: children.sort((a, b) => b.size - a.size),
      }))
      .sort(
        (a, b) =>
          b.children.reduce((s, c) => s + c.size, 0) -
          a.children.reduce((s, c) => s + c.size, 0),
      );

    return { groups: sectorGroups, totalValue: total, count: n, missingPrice };
  }, [items, scores, quotes, mode, sectorOf]);

  // Width drives the layout; height is fixed so the block never pushes the
  // table below the fold on a phone.
  useEffect(() => {
    if (collapsed) return undefined;
    const el = chartRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, groups.length]);

  const height = width && width < 560 ? 260 : 340;

  const cells = useMemo(() => {
    if (!width || !groups.length) return [];
    return layoutTwoLevel(groups, width, height);
  }, [groups, width, height]);

  if (!items?.length || !count) return null;

  const shareImage = async () => {
    setSharing('working');
    try {
      const blob = await renderHeatmapPng(groups, {
        modeLabel, colorFor, mode, positions: count,
      });
      if (!blob) throw new Error('no image');
      const copied = await shareHeatmapPng(blob);
      setSharing(copied ? 'copied' : 'saved');
    } catch {
      setSharing('');
    }
    setTimeout(() => setSharing(''), 4000);
  };

  const modeLabel = mode === 'today' ? 'today' : 'since you bought';

  // How old the stored analysis is. It is restored from localStorage, so it is
  // PER DEVICE: a laptop that ran the analysis and a phone that did not will
  // legitimately disagree, which is exactly what he hit on 2026-09-24.
  const ageHours = lastAnalyzed ? (Date.now() - new Date(lastAnalyzed).getTime()) / 3.6e6 : null;
  const stale = ageHours != null && ageHours > 24;
  let freshness = '';
  if (ageHours != null) {
    if (ageHours < 1) freshness = 'run in the last hour';
    else if (ageHours < 24) freshness = `run ${Math.round(ageHours)}h ago`;
    else freshness = `run ${Math.round(ageHours / 24)} day${Math.round(ageHours / 24) === 1 ? '' : 's'} ago`;
  }

  return (
    <div className="pf-heatmap">
      <div className="pf-heatmap-head">
        <div>
          <h3 className="pf-heatmap-title">Your holdings</h3>
          <p className="pf-heatmap-sub">
            Tile size is how much of your money is in the position. Colour is{' '}
            {modeLabel}.
          </p>
        </div>
        <div className="pf-heatmap-controls">
          <div className="pf-heatmap-modes" role="group" aria-label="Colour by">
            <button
              type="button"
              className={mode === 'total' ? 'active' : ''}
              onClick={() => setMode('total')}
            >
              Since you bought
            </button>
            <button
              type="button"
              className={mode === 'today' ? 'active' : ''}
              onClick={() => setMode('today')}
            >
              Today
            </button>
          </div>
          {!collapsed && (
            <button
              type="button"
              className="pf-heatmap-share"
              onClick={shareImage}
              disabled={sharing === 'working'}
              title="Save a PNG of this heatmap to attach to a post"
            >
              {sharing === 'working' ? 'Rendering…'
                : sharing === 'copied' ? 'Copied + saved'
                : sharing === 'saved' ? 'Saved'
                : 'Share image'}
            </button>
          )}
          <button
            type="button"
            className="pf-heatmap-collapse"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {mode === 'today' && quotesLoading && (
            <div className="pf-heatmap-note">Fetching today's moves…</div>
          )}

          <div className="pf-heatmap-canvas" ref={chartRef} style={{ height }}>
            {cells.map((cell) => {
              const showLabel = cell.w > 40 && cell.h > 24;
              const showPct = cell.w > 56 && cell.h > 40;
              const weight = totalValue > 0 ? (cell.value / totalValue) * 100 : 0;
              return (
                <div
                  key={`${cell.sector}-${cell.name}`}
                  className="pf-heatmap-cell"
                  style={{
                    left: cell.x,
                    top: cell.y,
                    width: Math.max(cell.w - 2, 0),
                    height: Math.max(cell.h - 2, 0),
                    background: colorFor(cell.pct, mode),
                  }}
                  title={
                    `${cell.name} · ${cell.sector}\n` +
                    `${fmtMoney(cell.value)} (${weight.toFixed(1)}% of portfolio)\n` +
                    `${fmtPct(cell.pct)} ${modeLabel}`
                  }
                >
                  {showLabel && <span className="pf-cell-symbol">{cell.name}</span>}
                  {showPct && <span className="pf-cell-pct">{fmtPct(cell.pct)}</span>}
                </div>
              );
            })}
          </div>

          {/* Where these numbers come from, and what is and is not live.
              Added 2026-09-25 at his request. He asked for a note saying the
              reading is only accurate after an analysis, and thought the Today
              view was exempt. It is not, quite: Today's COLOURS are live
              quotes, but tile SIZE is position_value out of the stored
              analysis in both modes, and sizing is this chart's main claim.
              A heatmap whose boxes are the wrong size is wrong in the way that
              matters most, so the caveat names sizes explicitly. */}
          <p className="pf-heatmap-source">
            {mode === 'today'
              ? "Today's moves are live. Tile sizes come from your last analysis"
              : 'Sizes and returns come from your last analysis'}
            {freshness ? ` (${freshness})` : ''}
            {'. '}
            {stale
              ? 'Re-run it to bring these up to date.'
              : 'Re-run the analysis after you add or change a position.'}
          </p>

          <p className="pf-heatmap-foot">
            {count} position{count === 1 ? '' : 's'} · {fmtMoney(totalValue)} total.
            {mode === 'total'
              ? ' Biggest tiles are where most of your money sits, so that is where a move matters most.'
              : " Grey tiles are positions today's quote did not cover."}
            {/* Say it rather than quietly drawing them flat. This is what made
                the same portfolio read differently on two devices. */}
            {missingPrice > 0 && (
              <>
                {' '}
                <strong>
                  {missingPrice} position{missingPrice === 1 ? ' has' : 's have'} no
                  price yet
                </strong>
                , shown grey. Run the analysis on this device to fill them in.
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}
